import { filterOption, ProtectPlusDeviceModal, type DeviceOption } from '@/entities/device';
import {
  addNewIcon,
  arrowIcon,
  closeIcon,
  ExternalLinkIcon,
  inforBlueIcon,
} from '@/shared/ui/assets';
import {
  InfoAlert,
  DatePickerInput,
  TextInput,
  ConfirmModal,
  PDFViewer,
  CustomButton,
  CustomSelect,
  RadioGroup,
  DiagnosticYieldAlert,
} from '@/shared/ui/components';
import { CreateReferringPhysicianDrawer } from '@/features/create-referring-physician';

import {
  RING_ID_DEVICE_SCHEMA,
  TIME_UNIT_OPTIONS,
  FOLLOW_ON_STUDY_TYPE,
  STUDY_TYPE_OPTIONS,
} from '@/entities/study';
import { zeroPad } from '@/shared/lib';
import { Space, Switch } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { forwardRef } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useNewStudyStep3 } from './useNewStudyStep3';
import { renderNotFoundContent, shouldShowStudyTypeWarningText } from '../../lib/newStudyStep3';
import NewStudyStepSkeleton from '../newStudyStepSkeleton/newStudyStepSkeleton';
import '../style.scss';
import type { FacilityOption, NewStudyStep3Props } from './newStudyStep3.types';
import './newStudyStep3.scss';
import WarningMessage from './warningMessage/warningMessage';

/**
 * Step 3 of the create-study wizard, collecting facility, device, physicians, study type and duration and report settings. Renders NewStudyStepSkeleton until props.isLoadedData, then a useNewStudyStep3-driven form with test study, assign-device-later and Bioflux Direct switches, paged device and physician search, duration inputs branched on follow-on and second-Holter cases, diagnostic-yield alerts, a PDF sample report swap, and firmware, returned device and device error modals.
 *
 * @returns Step 3 form element, or the loading skeleton while step data is being fetched.
 */
const NewStudyStep3 = forwardRef((props: NewStudyStep3Props, ref) => {
  const {
    state,
    setState,
    hasChangedDevice,
    deviceIdSelectRef,
    searchPhysicianTimeouts,
    isCardioOne,
    deviceModelList,
    isIcorDevice,
    studyTypeOptions,
    isWebView,
    isFollowOnStudyType,
    canSelectDurationUnit,
    isSecondHolterStudy,
    isMissingEmailWhenUseBiodirect,
    isMissingEmailWhenDeviceBiotresAndOutsideUS,
    toggleDisplayCreateReferringPhysicianDrawer,
    toggleDisplaySampleReport,
    handleCloseStudyDurationAlert,
    handleCloseFOStudyDurationAlert,
    downloadSampleReport,
    handleAssignDeviceLaterChange,
    handleUseBiodirectChange,
    onChange,
    onChangeSelection,
    handleSearchReferringPhysician,
    handleSearchInterprettingPhysician,
    handleScrollReferringPhysician,
    handleScrollInterprettingPhysician,
    handleDeviceChange,
    onChangeReportDueDate,
    handleClickCreateReferringPhysician,
    onClickBottomButtons,
    toggleDisplayWarningModal,
    handleRightButtonClickDeviceErrorModal,
    onChangeFacility,
    onClickCancelPatientEmailModal,
    onClickCancelReturnedDeviceModal,
    onClickConfirmReturnedDeviceModal,
    onClickOkayFunctionalTest,
    onClickCancelProtectPlusDevice,
    onClickConfirmProtectPlusDevice,
    handleDeviceIdSearch,
    handleDeviceIdDropdownVisibleChange,
    getReportDueDateBelowLimit,
    reportDueDateMessage,
  } = useNewStudyStep3(props, ref);

  /**
   * Builds the advisory message shown under the device selection in step 3 of the create-study wizard. Renders a blue info block telling the user a device can be assigned later from the Unassigned tab when device assignment is pending without Bioflux Direct, renders a WarningMessage in Bioflux Direct mode when assignment is not pending, and otherwise renders nothing.
   *
   * @returns Info or warning message element, or null when neither condition holds.
   */
  const infoMessageRenderer = () => {
    if (state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
      return (
        <div className="assign-device-later-message-container">
          <img src={inforBlueIcon} alt="Blue info icon" />
          {t`A device is required for the study to proceed. You can assign one later from the Unassigned tab.`}
        </div>
      );
    }

    if (state.isBiofluxDirect && !state.isDeviceAssignmentPending) {
      return <WarningMessage isBiofluxDirect />;
    }

    return null;
  };

  if (!props.isLoadedData) {
    return <NewStudyStepSkeleton />;
  }

  return (
    <div className="step-3">
      <CreateReferringPhysicianDrawer
        visible={state.isVisibleDrawer}
        countries={props.countries}
        data={state.newStudyStep3Data}
        handleClickClose={toggleDisplayCreateReferringPhysicianDrawer}
        handleClickCreate={handleClickCreateReferringPhysician}
        isCreatingReferringPhysician={state.isCreatingReferringPhysician}
      />

      <ProtectPlusDeviceModal
        isOpen={state.isOpenProtectPlusDeviceModal}
        onClickCancelButton={onClickCancelProtectPlusDevice}
        onClickConfirmButton={onClickConfirmProtectPlusDevice}
      />
      <ConfirmModal
        isOpen={state.isOpenWarningModal}
        onClickRightButton={() => {
          toggleDisplayWarningModal('firmware');
          if (hasChangedDevice.current) {
            hasChangedDevice.current = false;
            if (deviceIdSelectRef.current) {
              deviceIdSelectRef.current.focusAndOpenSelectInput();
            }
          }
        }}
        rightButtonName={t`Okay`}
        title={t`Firmware update required`}
        question={t`This study type is not supported by the current firmware of this device. Please update the firmware to its latest version.`}
      />
      <ConfirmModal
        isOpen={state.isOpenWarningAutoEventModal}
        onClickRightButton={() => {
          toggleDisplayWarningModal('auto-event');
          if (hasChangedDevice.current) {
            hasChangedDevice.current = false;
            if (deviceIdSelectRef.current) {
              deviceIdSelectRef.current.focusAndOpenSelectInput();
            }
          }
        }}
        rightButtonName={t`Okay`}
        title={t`Feature not available`}
        question={t`Please contact your sales representative to enable this feature.`}
      />
      <ConfirmModal
        className="warning-modal returned-device-modal"
        isRedBtn={false}
        isBoldLeftBtn
        isOpen={state.isOpenReturnedDeviceModal}
        isFilledRightButton
        rightButtonName={t`Continue`}
        leftButtonName={t`Cancel`}
        onClickLeftButton={onClickCancelReturnedDeviceModal}
        onClickRightButton={onClickConfirmReturnedDeviceModal}
        title={t`Confirm returned device`}
        isSaving={state.isSavingReturnedDeviceModal}
        customContent={
          <div className="returned-device-modal__content">
            {state.isSavingReturnedDeviceModal && <div className="saving-background" />}
            <span>
              {t`By clicking “Continue” implies that you have confirmed the device has been returned with all the accompanying items. Are you sure you want to proceed?`}
            </span>
            {state.returnedDeviceDataModal?.isViewGotoFleetManagement && (
              <div className="returned-device-modal__action">
                {t`Need more consideration?`}{' '}
                <a
                  target="_blank"
                  onClick={onClickCancelReturnedDeviceModal}
                  href={`/studies/${state.returnedDeviceDataModal?.studyId}`}
                  rel="noreferrer"
                >
                  {t`Go to device's previous study details.`}
                </a>
              </div>
            )}
          </div>
        }
      />
      <ConfirmModal
        isOpen={state.isOpenFunctionalTestModal}
        onClickRightButton={onClickOkayFunctionalTest}
        rightButtonName={t`Okay`}
        title={t`Notice`}
        question={t`You can only create a MCT or Cardiac study. Please select another study type.`}
      />
      <ConfirmModal
        title={t`Missing patient email`}
        question={
          <div>
            {isMissingEmailWhenUseBiodirect
              ? t`The patient email is required for Biodirect study.`
              : t`Patients located outside the United States must provide an email address to receive a Biocore study reference code.`}{' '}
            Press&nbsp;
            <strong className="bold-text">Enter Now&nbsp;</strong>
            <span>to go back to&nbsp;</span>
            <strong className="bold-text">Patient Contact&nbsp;</strong>
            <span>step and complete the field.</span>
          </div>
        }
        isOpen={state.isOpenPatientEmailModal}
        leftButtonName={t`Cancel`}
        rightButtonName={t`Enter Now`}
        onClickLeftButton={onClickCancelPatientEmailModal}
        onClickRightButton={() => {
          onClickBottomButtons(false);
        }}
      />
      <ConfirmModal
        isOpen={state.isOpenDeviceErrorModal}
        onClickRightButton={handleRightButtonClickDeviceErrorModal}
        rightButtonName={t`Got It`}
        title={t`Cannot select this device`}
        question={state.deviceErrorModalMessage}
      />

      {!state.isShowSampleReport ? (
        <div className="step-content">
          <div className="centered-content">
            <div className="centered-content__body">
              {state.formattedSupportFacilities.length > 1 && (
                <CustomSelect
                  id="facility"
                  className="facility-select"
                  title={t`Facility`}
                  aria-label={t`Facility`}
                  allowClear={false}
                  name="selectedFacility"
                  size="middle"
                  options={state.formattedSupportFacilities}
                  value={state.selectedFacility}
                  onChange={(name, value, option) =>
                    onChangeFacility(name, value, option as unknown as FacilityOption)
                  }
                  onDropdownVisibleChange={() => {}}
                />
              )}
              {state.selectedFacility?.status === 'Test' && (
                <div className="switch-group">
                  <Switch
                    id="functional-test"
                    className="switch-btn"
                    checked={state.isTest}
                    disabled={props.isChangingFacility}
                    onChange={(checked) => {
                      onChange('isTest', checked);
                    }}
                    aria-label={t`Functional test`}
                  />
                  <div>{t`Functional test`}</div>
                </div>
              )}
              {!state.isBiofluxDirect && (
                <div className="switch-group">
                  <Switch
                    id="assign-device-later"
                    className="switch-btn"
                    disabled={props.isChangingFacility}
                    checked={state.isDeviceAssignmentPending}
                    onChange={handleAssignDeviceLaterChange}
                    aria-label={t`Assign device later`}
                  />
                  <div>{t`Assign device later`}</div>
                </div>
              )}
              {state.newStudyStep3Data.facility?.canUseBiofluxDirect &&
                !state.isDeviceAssignmentPending && (
                  <div className="biodirect-switch-input-container">
                    <div className="switch-group">
                      <Switch
                        id="use-biodirect"
                        aria-label={t`Use Biodirect`}
                        className="switch-btn"
                        checked={state.isBiofluxDirect}
                        disabled={props.isChangingFacility}
                        onChange={handleUseBiodirectChange}
                      />
                      <div>{t`Use Biodirect (added charge shall be applied)`}</div>
                    </div>
                  </div>
                )}
              {infoMessageRenderer()}
              {!state.isDeviceAssignmentPending && (
                <>
                  <div className="device-model-container">
                    <div className="label">{t`Device type`}</div>
                    <div className="list-device-types">
                      <RadioGroup
                        value={state.newStudyStep3Data.model}
                        name="model"
                        className="radio-input device-model-input"
                        onChange={onChange}
                        options={deviceModelList}
                        size="small"
                        disabled={props.isChangingFacility || isCardioOne}
                      />
                    </div>
                  </div>
                  <WarningMessage
                    isMissingEmailWhenDeviceBiotresAndOutsideUS={
                      isMissingEmailWhenDeviceBiotresAndOutsideUS
                    }
                  />
                </>
              )}
              {!state.isDeviceAssignmentPending &&
                !state.isBiofluxDirect &&
                (isIcorDevice ? (
                  <>
                    <TextInput
                      name="deviceId"
                      label={t`Device ID`}
                      placeholder={t`Enter device ID`}
                      value={state.newStudyStep3Data.deviceId ?? ''}
                      onChange={onChange}
                      isError={!!state.errorObject.deviceId}
                      errorMessage={state.errorObject.deviceId}
                      isKeyboardNumberMobile={isWebView}
                    />
                    <WarningMessage isIcorDevice />
                  </>
                ) : (
                  <>
                    <div className="device-id-select-input-container-wrapper">
                      <CustomSelect
                        id="device-id"
                        ref={deviceIdSelectRef}
                        className="device-id-select"
                        title={t`Device ID`}
                        aria-label={t`Device ID`}
                        allowClear={false}
                        popupClassName="device-id-list-select"
                        name="deviceId"
                        size="middle"
                        searchValue={state.searchDeviceId}
                        onSearch={handleDeviceIdSearch}
                        onDropdownVisibleChange={handleDeviceIdDropdownVisibleChange}
                        options={state.devicesOptions}
                        value={
                          props.isLoadingDevice || props.isChangingFacility
                            ? t`Loading...`
                            : state.deviceId?.isHighSamplingRate
                              ? state.deviceId.value
                              : state.deviceId || null
                        }
                        filterOption={(inputValue, option) =>
                          filterOption(
                            inputValue,
                            (option ?? null) as unknown as DeviceOption | null,
                          )
                        }
                        onChange={handleDeviceChange as any}
                        disabled={
                          state.isBiofluxDirect || props.isLoadingDevice || props.isChangingFacility
                        }
                        placeholder={
                          props.isLoadingDevice || props.isChangingFacility
                            ? t`Loading...`
                            : t`Search & Select a device ID`
                        }
                        isError={!!state.errorObject.deviceId}
                        errorMessage={state.errorObject.deviceId}
                        notFoundContent={renderNotFoundContent({
                          isSearching: state.isSearchingDevice,
                          searchDeviceId: state.searchDeviceId,
                        })}
                        infoMessage={
                          state.deviceId?.isHighSamplingRate &&
                          t`This device is operating at a high sampling rate.`
                        }
                      />
                    </div>
                    {zeroPad(state.deviceId?.schema, 8) === RING_ID_DEVICE_SCHEMA && (
                      <div>
                        <TextInput
                          name="ringId"
                          type="number"
                          label={t`Ring ID`}
                          placeholder={t`Enter ring ID`}
                          value={state.newStudyStep3Data.ringId}
                          onChange={onChange}
                          isError={!!state.errorObject.ringId}
                          errorMessage={state.errorObject.ringId}
                          isKeyboardNumberMobile={isWebView}
                          isNumberFormat={isWebView}
                        />
                      </div>
                    )}
                  </>
                ))}
              <CustomSelect
                id="referring-physician"
                className="referring-physician-select"
                title={t`Referring physician`}
                aria-label={t`Referring physician`}
                allowClear={false}
                name="referringPhysician"
                size="middle"
                popupClassName="referring-physician-list-select"
                showSearch
                onSearch={handleSearchReferringPhysician}
                filterOption={false}
                options={state.referringPhysiciansOptions}
                onPopupScroll={handleScrollReferringPhysician}
                // oxlint-disable-next-line react/no-unstable-nested-components -- antd render prop, not a component type
                dropdownRender={(menu) => (
                  <>
                    <div
                      className="add-new-physician"
                      aria-hidden
                      onClick={toggleDisplayCreateReferringPhysicianDrawer}
                    >
                      {t`Add new physician`}
                      <img src={addNewIcon} alt="addNewIcon" />
                    </div>
                    {menu}
                  </>
                )}
                value={props.isChangingFacility ? t`Loading...` : state.referringPhysician}
                onChange={(name, value, option) => {
                  const physicianOption = option as {
                    label: string;
                    value: string;
                    firstName?: string;
                    lastName?: string;
                  };
                  const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
                  updatedNewStudyStep3Data.referringPhysician = physicianOption?.value;
                  clearTimeout(searchPhysicianTimeouts.current.referring);
                  setState({
                    referringPhysician: physicianOption,
                    referringPhysicianFirstName: physicianOption.firstName,
                    referringPhysicianLastName: physicianOption.lastName,
                    newStudyStep3Data: updatedNewStudyStep3Data,
                    searchReferringPhysician: '',
                  });
                }}
                disabled={props.isChangingFacility}
                loading={state.isLoadingReferringPhysician}
                isError={!!state.errorObject.referringPhysician}
                errorMessage={state.errorObject.referringPhysician}
                placeholder={
                  props.isChangingFacility
                    ? t`Loading...`
                    : t`Search by name or NPI to select physician`
                }
                notFoundContent={
                  <div className="empty-select">
                    {state.isLoadingReferringPhysician
                      ? t`Loading`
                      : state.searchReferringPhysician
                        ? t`No physician matches your search`
                        : t`Search by name or NPI`}
                  </div>
                }
                onDropdownVisibleChange={() => {}}
              />
              <CustomSelect
                id="interpretting-physician"
                title={t`Interpreting physician`}
                aria-label={t`Interpreting physician`}
                allowClear={false}
                name="interprettingPhysician"
                size="middle"
                showSearch
                onSearch={handleSearchInterprettingPhysician}
                filterOption={false}
                options={state.interprettingPhysiciansOptions}
                onPopupScroll={handleScrollInterprettingPhysician}
                placeholder={
                  props.isChangingFacility
                    ? t`Loading...`
                    : t`Search by name or NPI to select physician`
                }
                value={props.isChangingFacility ? t`Loading...` : state.interprettingPhysician}
                onChange={(name, value, option) => {
                  const physicianOption = option as {
                    label: string;
                    value: string;
                    firstName?: string;
                    lastName?: string;
                  };
                  const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
                  updatedNewStudyStep3Data.interprettingPhysician = physicianOption?.value;
                  clearTimeout(searchPhysicianTimeouts.current.interpreting);
                  setState({
                    interprettingPhysician: physicianOption,
                    interprettingPhysicianFirstName: physicianOption.firstName,
                    interprettingPhysicianLastName: physicianOption.lastName,
                    newStudyStep3Data: updatedNewStudyStep3Data,
                    searchInterprettingPhysician: '',
                  });
                }}
                isError={!!state.errorObject.interprettingPhysician}
                errorMessage={state.errorObject.interprettingPhysician}
                disabled={props.isChangingFacility}
                loading={state.isLoadingInterprettingPhysician}
                notFoundContent={
                  <div className="empty-select">
                    {state.isLoadingInterprettingPhysician
                      ? t`Loading`
                      : state.searchInterprettingPhysician
                        ? t`No physician matches your search`
                        : t`Search by name or NPI`}
                  </div>
                }
                onDropdownVisibleChange={() => {}}
              />
              {isFollowOnStudyType ? (
                <>
                  <CustomSelect
                    id="study-type"
                    title={t`Study type`}
                    aria-label={t`Study type`}
                    showSearch
                    allowClear={false}
                    name="studyType"
                    size="middle"
                    options={STUDY_TYPE_OPTIONS}
                    placeholder={t`Select study type`}
                    value={state.studyType}
                    onChange={(name, value, option) =>
                      onChangeSelection(name, value, option as { label: string; value: string })
                    }
                    isError={!!state.errorObject.studyType}
                    errorMessage={state.errorObject.studyType}
                    onDropdownVisibleChange={() => {}}
                  />
                  <div className="primary-study-type-container">
                    <div className="primary-study-type-list">
                      <CustomSelect
                        id="primary-study-type"
                        title={t`Primary study type`}
                        aria-label={t`Primary study type`}
                        showSearch
                        allowClear={false}
                        name="primaryStudyType"
                        size="middle"
                        options={_.filter(
                          STUDY_TYPE_OPTIONS,
                          (x) => x.value !== FOLLOW_ON_STUDY_TYPE,
                        )}
                        placeholder={t`Select study type`}
                        value={state.primaryStudyType}
                        onChange={(name, value, option) =>
                          onChangeSelection(name, value, option as { label: string; value: string })
                        }
                        isError={!!state.errorObject.primaryStudyType}
                        errorMessage={state.errorObject.primaryStudyType}
                        onDropdownVisibleChange={() => {}}
                      />
                    </div>
                    <div
                      className="view-sample-report-btn"
                      aria-hidden
                      onClick={() => {
                        downloadSampleReport(true);
                      }}
                    >
                      <img src={ExternalLinkIcon} alt="External link icon" />
                      {t`View sample report`}
                    </div>
                  </div>
                  <WarningMessage
                    shouldShowStudyTypeWarningText={shouldShowStudyTypeWarningText(
                      state.primaryStudyType?.value ?? '',
                    )}
                  />
                </>
              ) : (
                <>
                  <div>
                    <CustomSelect
                      id="study-type"
                      title={t`Study type`}
                      aria-label={t`Study type`}
                      showSearch
                      allowClear={false}
                      name="studyType"
                      size="middle"
                      options={studyTypeOptions}
                      placeholder={t`Select study type`}
                      value={state.studyType}
                      onChange={(name, value, option) =>
                        onChangeSelection(name, value, option as { label: string; value: string })
                      }
                      isError={!!state.errorObject.studyType}
                      errorMessage={state.errorObject.studyType}
                      onDropdownVisibleChange={() => {}}
                    />
                    <div
                      className="view-sample-report-btn"
                      aria-hidden
                      onClick={() => {
                        downloadSampleReport(true);
                      }}
                    >
                      <img src={ExternalLinkIcon} alt="External link icon" />
                      {t`View sample report`}
                    </div>
                  </div>
                  <WarningMessage
                    shouldShowStudyTypeWarningText={shouldShowStudyTypeWarningText(
                      state?.studyType?.value ?? '',
                    )}
                  />
                </>
              )}
              {canSelectDurationUnit ? (
                <Space.Compact>
                  <TextInput
                    id="study-duration"
                    name="studySettingDuration"
                    type="number"
                    label={isFollowOnStudyType ? t`Primary study duration` : t`Study duration`}
                    placeholder={t`Enter study duration`}
                    value={state.studySettingDuration}
                    onChange={onChange}
                    isError={!!state.errorObject.studySettingDuration}
                    errorMessage={state.errorObject.studySettingDuration}
                    isKeyboardNumberMobile={isWebView}
                    isNumberFormat={isWebView}
                  />
                  <CustomSelect
                    id="study-duration-unit"
                    name="unitTime"
                    className="option-unit"
                    onChange={(name, value, option) =>
                      onChangeSelection(name, value, option as { label: string; value: string })
                    }
                    value={state.unitTime}
                    defaultValue="day"
                    options={TIME_UNIT_OPTIONS}
                    size="middle"
                    allowClear={false}
                    showSearch={false}
                    popupMatchSelectWidth={30}
                    suffixIcon={<img src={arrowIcon} alt="arrow icon" />}
                    onDropdownVisibleChange={() => {}}
                    aria-label={t`Study duration unit`}
                  />
                </Space.Compact>
              ) : (
                <>
                  <TextInput
                    id="study-duration"
                    name="studySettingDuration"
                    type="number"
                    label={isFollowOnStudyType ? t`Primary study duration` : t`Study duration`}
                    placeholder={t`Enter study duration`}
                    value={state.studySettingDuration}
                    onChange={onChange}
                    isError={!!state.errorObject.studySettingDuration}
                    errorMessage={state.errorObject.studySettingDuration}
                    isKeyboardNumberMobile={isWebView}
                    isNumberFormat={isWebView}
                    decimalScale={5}
                    suffix="day(s)"
                  />
                  {state.deviceId?.isHighSamplingRate && (
                    <InfoAlert
                      message={t`This device is operating at a high sampling rate, so the maximum study duration is 7 days.`}
                    />
                  )}
                  {state.isShowedStudyDurationAlert && (
                    <DiagnosticYieldAlert afterClose={handleCloseStudyDurationAlert} />
                  )}
                </>
              )}
              {!_.isNil(state.newStudyStep3Data.followOnStudyType) &&
                state.studyType?.value === FOLLOW_ON_STUDY_TYPE && (
                  <>
                    <div>
                      <CustomSelect
                        id="follow-on-study-type"
                        title={t`Follow-on study type`}
                        aria-label={t`Follow-on study type`}
                        showSearch
                        allowClear={false}
                        name="followOnStudyType"
                        size="middle"
                        options={_.filter(
                          STUDY_TYPE_OPTIONS,
                          (x) => x.value !== FOLLOW_ON_STUDY_TYPE,
                        )}
                        placeholder={t`Select follow-on study type`}
                        value={state.followOnStudyType}
                        onChange={(name, value, option) =>
                          onChangeSelection(name, value, option as { label: string; value: string })
                        }
                        isError={!!state.errorObject.followOnStudyType}
                        errorMessage={state.errorObject.followOnStudyType}
                        onDropdownVisibleChange={() => {}}
                      />
                      <div
                        className="view-sample-report-btn"
                        aria-hidden
                        onClick={() => {
                          downloadSampleReport(false);
                        }}
                      >
                        <img src={ExternalLinkIcon} alt="External link icon" />
                        {t`View sample report`}
                      </div>
                    </div>
                    <WarningMessage
                      shouldShowStudyTypeWarningText={shouldShowStudyTypeWarningText(
                        state?.followOnStudyType?.value,
                      )}
                    />
                    {isSecondHolterStudy ? (
                      <Space.Compact>
                        <TextInput
                          id="follow-on-study-duration"
                          name="followOnStudyDuration"
                          type="number"
                          label={t`Follow-on study duration`}
                          placeholder={t`Enter follow-on study duration`}
                          value={state.followOnStudyDuration}
                          onChange={onChange}
                          isError={!!state.errorObject.followOnStudyDuration}
                          errorMessage={state.errorObject.followOnStudyDuration}
                        />
                        <CustomSelect
                          id="follow-on-study-duration-unit"
                          name="unitTimeFO"
                          className="option-unit"
                          onChange={(name, value, option) =>
                            onChangeSelection(
                              name,
                              value,
                              option as { label: string; value: string },
                            )
                          }
                          value={state.unitTimeFO}
                          defaultValue="day"
                          options={TIME_UNIT_OPTIONS}
                          size="middle"
                          allowClear={false}
                          showSearch={false}
                          popupMatchSelectWidth={30}
                          suffixIcon={<img src={arrowIcon} alt="arrow icon" />}
                          onDropdownVisibleChange={() => {}}
                          aria-label={t`Follow-on study duration unit`}
                        />
                      </Space.Compact>
                    ) : (
                      <>
                        <TextInput
                          id="follow-on-study-duration"
                          name="followOnStudyDuration"
                          type="number"
                          label={t`Follow-on study duration`}
                          placeholder={t`Enter follow-on study duration`}
                          value={state.followOnStudyDuration}
                          onChange={onChange}
                          isError={!!state.errorObject.followOnStudyDuration}
                          errorMessage={state.errorObject.followOnStudyDuration}
                          suffix="day(s)"
                        />
                        {state.deviceId?.isHighSamplingRate && (
                          <InfoAlert
                            message={t`This device is operating at a high sampling rate, so the maximum study duration is 7 days.`}
                          />
                        )}
                        {state.isShowedFOStudyDurationAlert && (
                          <DiagnosticYieldAlert afterClose={handleCloseFOStudyDurationAlert} />
                        )}
                      </>
                    )}
                  </>
                )}
              <DatePickerInput
                id="patient-return-visit"
                className="return-visit-input"
                name="reportDueDate"
                label={
                  state.isDeviceAssignmentPending || state.isBiofluxDirect
                    ? t`Patient return visit (optional)`
                    : t`Patient return visit`
                }
                placeholder="MM/DD/YYYY"
                value={state.reportDueDate ? dayjs(state.reportDueDate) : null}
                belowLimit={getReportDueDateBelowLimit()}
                onChange={onChangeReportDueDate}
                isError={!!state.errorObject.reportDueDate}
                errorMessage={state.errorObject.reportDueDate}
                isWarning={!!reportDueDateMessage}
                warningMessage={reportDueDateMessage}
              />
              <TextInput
                id="clinician-initials"
                name="clinicianInitials"
                label={t`Clinician initials`}
                placeholder={t`Enter clinician initials`}
                value={state.newStudyStep3Data.clinicianInitials}
                onChange={onChange}
                isError={!!state.errorObject.clinicianInitials}
                errorMessage={state.errorObject.clinicianInitials}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="step-content sample-report">
          <div className="header">
            <CustomButton
              id="close-btn"
              style="neutral"
              size="small"
              type="outline"
              icon={<ReactSVG src={closeIcon} />}
              onClick={toggleDisplaySampleReport}
              ariaLabel={t`Close`}
            />
            <div className="header-title">
              <strong className="content">{t`Sample Report`}</strong>
              <div className="study-type">{state.sampleReportName}</div>
            </div>
          </div>
          <div className="sample-report__body">
            <PDFViewer url={state?.pdfData?.url} />
          </div>
        </div>
      )}
    </div>
  );
});

export default NewStudyStep3;
